<template>
  <div :class="classes.wrapper">
    <label v-if="type === 'textarea'" :class="classes.label">
      <small :class="classes.text">
        <span :class="classes.textVisibility">{{ label }}</span>
        <span v-if="isRequired" class="color-red" aria-hidden>*</span>
      </small>
      <textarea
        :value="value"
        @input="onInput"
        v-bind="$attrs"
        v-on="listeners"
        :class="classes.input"
        placeholder=" "
        rows="3"
        :aria-describedby="descriptionId"
      ></textarea>
    </label>

    <label v-else-if="type === 'checkbox'" :class="classes.label">
      <input
        :checked="value"
        @change="onCheck"
        :type="type"
        v-bind="$attrs"
        v-on="listeners"
        class="visually-hidden"
        :class="classes.input"
        :aria-describedby="descriptionId"
      />
      <span :class="classes.text">
        <span :class="classes.textVisibility">{{ label }}</span>
        <span v-if="isRequired" class="color-red" aria-hidden>*</span>
      </span>
    </label>

    <label v-else-if="type === 'switch'" :class="classes.label">
      <input
        :checked="value"
        @change="onCheck"
        type="checkbox"
        v-bind="$attrs"
        v-on="listeners"
        class="visually-hidden"
        :class="classes.input"
        :aria-describedby="descriptionId"
      />
      <span class="mtx-input__switch" :aria-label="label"></span>
    </label>

    <template v-else-if="type === 'radio'">
      <div
        v-for="(option, i) in computedOptions"
        :key="i"
        v-bind="$attrs"
        class="radio-wrapper"
        :class="stacked ? 'radio-wrapper--stacked' : ''"
      >
        <label :class="classes.label">
          <input
            v-bind="option"
            :checked="option.value == value"
            @change="onInput"
            :type="type"
            v-on="listeners"
            class="visually-hidden"
            :class="classes.input"
            :aria-describedby="descriptionId"
          />
          <span :class="classes.text">{{ option.label }}</span>
        </label>
      </div>
    </template>

    <label v-else-if="type === 'select'" :class="classes.label">
      <small :class="classes.text">
        <span :class="classes.textVisibility">{{ label }}</span>
        <span v-if="isRequired" class="color-red" aria-hidden>*</span>
      </small>
      <select
        @change="onInput"
        v-bind="$attrs"
        v-on="listeners"
        :class="classes.input"
        :aria-describedby="descriptionId"
      >
        <option
          v-for="(option, i) in computedOptions"
          :key="i"
          v-bind="option"
          :selected="option.value == value"
        >
          {{ option.label }}
        </option>
      </select>
    </label>

    <template v-else-if="type === 'number'">
      <label :class="classes.label">
        <small :class="classes.text">
          <span :class="classes.textVisibility">{{ label }}</span>
          <span v-if="isRequired" class="color-red" aria-hidden>*</span>
        </small>
        <input
          :value="value"
          @input="onInput"
          @blur="minMaxBlur"
          :type="type"
          v-bind="$attrs"
          v-on="listeners"
          :class="classes.input"
          ref="input"
          :aria-describedby="descriptionId"
        />
      </label>
    </template>

    <label v-else :class="classes.label">
      <small :class="classes.text">
        <span :class="classes.textVisibility">{{ label }}</span>
        <span v-if="isRequired" class="color-red" aria-hidden>*</span>
      </small>
      <input
        :value="value"
        @input="onInput"
        :type="type"
        v-bind="$attrs"
        v-on="listeners"
        :class="classes.input"
        :aria-describedby="descriptionId"
      />
    </label>

    <p v-if="description" class="mb-0 color-muted">
      <small :id="descriptionId">{{ description }}</small>
    </p>

    <small v-if="validationErrors" class="color-red">{{ validationErrors }}</small>
  </div>
</template>

<script>
const allowedTypes = [
  "text",
  "password",
  "email",
  "number",
  "textarea",
  "radio",
  "checkbox",
  "switch",
  "search",
  "select",
  "url"
]

export default {
  inheritAttrs: false, // TODO: should we use this?

  props: {
    value: {
      type: [String, Number, Boolean],
      default: ""
    },
    type: {
      type: String,
      default: "text",
      validator(value) {
        return allowedTypes.includes(value)
      }
    },
    label: String,
    options: {
      type: Array,
      default: () => []
    },
    description: String,
    required: Boolean,
    stacked: Boolean,
    validation: Object,
    errors: Object,
    hideLabel: Boolean
  },

  data: () => ({
    timeoutId: null
  }),

  computed: {
    classes() {
      const base = "mtx-input"
      return {
        base,
        wrapper: `${base} ${base}--${this.type}`,
        input: `${base}__input ${base}__input--${this.type}`,
        label: `${base}__label ${base}__label--${this.type}`,
        text: `${base}__text ${base}__text--${this.type}`,
        textVisibility: this.hideLabel ? " visually-hidden" : ""
      }
    },

    listeners() {
      // Prevent input listener from overwriting our local input event
      // eslint-disable-next-line no-unused-vars
      const { input, ...listeners } = this.$listeners
      return listeners
    },

    computedOptions() {
      return this.options.map(item => {
        // Each item should be an object with at least value and label which we can bind to later
        item = typeof item === "object" ? item : { value: item }
        item.label = item.label || item.value
        item.name = this.$attrs.name
        return item
      })
    },

    isRequired() {
      return this.required || (this.validation && this.validation.hasOwnProperty("required"))
    },

    validationErrors() {
      if (!this.validation || !this.validation.$error) return ""

      const { $params } = this.validation
      const errorMap = {
        required: "This field is required.",
        requiredIf: "This field is required.",
        minLength: `Cannot be shorter than ${$params.minLength &&
          $params.minLength.min} characters.`,
        maxLength: `Cannot be longer than ${$params.maxLength &&
          $params.maxLength.max} characters.`,
        between: `Value must be between ${$params.between &&
          $params.between.min} and ${$params.between && $params.between.max}.`,
        email: `Must be a valid email address.`,
        ethAddress: `Must be a valid Ethereum address.`,
        ipfsHash: `Must be a valid IPFS hash.`,
        shaHash: `Must be a valid hash.`,
        name: `Special characters not allowed.`,
        ...this.errors
      }

      const errors = []
      // Loop over each validator
      Object.keys($params).forEach(key => {
        // Check if this validator entry is invalid
        if (!this.validation[key]) {
          // Add custom error message based on validator type
          const errorType = $params[key] ? $params[key].type : key
          const error = errorMap[errorType] || `Validation error: ${errorType}`
          errors.push(error)
        }
      })

      if (!errors.length) return "This field has some errors"

      return errors.join(" ")
    },

    descriptionId() {
      return Array(6)
        .fill()
        .map(() => Math.floor(36 * Math.random()).toString(36))
        .join("")
    }
  },

  created() {
    if (this.type === "radio" && !this.$attrs.name) {
      // Radios don't work properly without a name. If on is not provided, randomly generate one.
      this.$attrs.name = [...Array(6)]
        .map(() => Math.floor(36 * Math.random()).toString(36))
        .join("")
    }
  },

  methods: {
    onInput(event) {
      const el = event.target

      /* istanbul ignore next */
      if (this.type === "textarea") {
        // Resize textarea to fit content
        const borderHeight = el.offsetHeight - el.clientHeight
        el.style.height = "auto"
        el.style.height = el.scrollHeight + borderHeight + "px"
      }

      this.$emit("input", el.value)
    },

    minMaxBlur(event) {
      const el = event.target
      if (this.type === "number" && el.value) {
        if (typeof this.$attrs.max !== "undefined") {
          el.value = Math.min(+this.$attrs.max, +el.value)
        }

        if (typeof this.$attrs.min !== "undefined") {
          el.value = Math.max(+this.$attrs.min, +el.value)
        }

        el.value = el.value || 0
        this.$emit("input", event.target.value)
      }
      this.$listeners.blur && this.$listeners.blur(event)
    },

    onCheck(event) {
      this.$emit("input", event.target.checked)
    }
  }
}
</script>

<style lang="scss">
.mtx-input {
  &__input {
    &--textarea {
      display: block;
    }
  }

  &__text {
    &--checkbox,
    &--radio {
      position: relative;
      padding-left: 1.5em;

      &:before,
      &:after {
        display: inline-block;
        position: absolute;
      }

      &:before {
        content: "";
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        width: 1.2em;
        height: 1.2em;
        border: 2px solid $stroke;
        border-radius: $borderRadius;
      }

      input:checked + &:before {
        border-color: $blue;
      }

      input:focus + &:before {
        @include focus;
      }

      input:disabled + & {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    &--checkbox {
      input:checked + &:before {
        background-color: $blue;
      }

      input:checked + &:after {
        content: "";
        top: 0.1em;
        left: 0.4em;
        transform: rotate(45deg);
        width: 0.4em;
        height: 0.8em;
        border-right: 0.1em solid #fff;
        border-bottom: 0.1em solid #fff;
      }
    }

    &--radio {
      &:before,
      &:after {
        border-radius: 50%;
      }

      input:checked + &:after {
        content: "";
        top: 50%;
        left: 0.3em;
        transform: translateY(-50%);
        width: 0.6em;
        height: 0.6em;
        background: $highlight;
      }
    }

    // .radio-wrapper {
    //   display: inline;
    //   margin-right: 1rem;

    //   &--stacked {
    //     display: block;
    //     margin-right: 0;
    //   }
    // }
  }

  &--switch {
    line-height: 0; // Makes sure the element is only as tall as it's contents
  }

  &__switch {
    display: inline-block;
    position: relative;
    width: 2em;
    height: 1em;
    border: 1px solid $grey;
    border-radius: 5em;
    background: #fff;
    transition: background 0.3s ease;

    &:before {
      content: "";
      display: block;
      position: absolute;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
      width: 1em;
      height: 1em;
      border-radius: 50%;
      background: #fff;
      box-shadow: 0 0 0 1px $grey;
      transition: transform 0.3s ease;
    }

    input:checked + & {
      background: $highlight;

      &:before {
        transform: translate(100%, -50%);
      }
    }

    input:focus + & {
      @include focus;
    }

    input:disabled + & {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
